import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, OverlayTrigger, Tooltip, ProgressBar, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate, formatDateTime } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function MouvementsStock() {
    const navigate = useNavigate();
    const [mouvements, setMouvements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [stats, setStats] = useState(null);
    const [viewMode, setViewMode] = useState('table');
    const [sortConfig, setSortConfig] = useState({ key: 'date_mouvement', direction: 'desc' });

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchMouvements();
        fetchStats();
    }, [filterType, dateDebut, dateFin]);

    const fetchMouvements = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/stock_etat.php?action=mouvements';
            
            const params = new URLSearchParams();
            if (filterType) params.append('type_mouvement', filterType);
            if (dateDebut) params.append('date_debut', dateDebut);
            if (dateFin) params.append('date_fin', dateFin);
            
            if (params.toString()) url += '&' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setMouvements(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stock_etat.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getTypeBadge = (type) => {
        const configs = {
            entree: { 
                bg: 'success', 
                icon: 'ri-arrow-down-line', 
                label: 'Entrée',
                gradient: 'linear-gradient(135deg, #10b981, #059669)'
            },
            sortie: { 
                bg: 'danger', 
                icon: 'ri-arrow-up-line', 
                label: 'Sortie',
                gradient: 'linear-gradient(135deg, #dc3545, #bb2d3b)'
            },
            inventaire: { 
                bg: 'info', 
                icon: 'ri-survey-line', 
                label: 'Inventaire',
                gradient: 'linear-gradient(135deg, #0dcaf0, #0b9cbe)'
            },
            reservation: { 
                bg: 'warning', 
                icon: 'ri-lock-line', 
                label: 'Réservation',
                gradient: 'linear-gradient(135deg, #f59e0b, #d97706)'
            },
            annulation: { 
                bg: 'secondary', 
                icon: 'ri-close-line', 
                label: 'Annulation',
                gradient: 'linear-gradient(135deg, #6c757d, #5a6268)'
            }
        };
        const config = configs[type] || configs.sortie;
        
        return (
            <Badge 
                className="px-3 py-2 rounded-pill"
                style={{
                    background: config.gradient,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredMouvements = mouvements
        .filter(m => 
            m.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
            m.designation?.toLowerCase().includes(filterText.toLowerCase())
        )
        .sort((a, b) => {
            if (sortConfig.key === 'date_mouvement') {
                const dateA = new Date(a.date_mouvement);
                const dateB = new Date(b.date_mouvement);
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }
            if (sortConfig.key === 'quantite') {
                return sortConfig.direction === 'asc' 
                    ? (a.quantite || 0) - (b.quantite || 0)
                    : (b.quantite || 0) - (a.quantite || 0);
            }
            return 0;
        });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredMouvements.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredMouvements.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    // Calcul des statistiques
    const totalEntrees = mouvements
        .filter(m => m.type_mouvement === 'entree')
        .reduce((sum, m) => sum + (m.quantite || 0), 0);
    
    const totalSorties = mouvements
        .filter(m => m.type_mouvement === 'sortie')
        .reduce((sum, m) => sum + (m.quantite || 0), 0);

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des mouvements...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchMouvements}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(13, 202, 240, 0.15), rgba(11, 156, 190, 0.15))',
                                            boxShadow: '0 10px 20px rgba(13, 202, 240, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-history-line text-info" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Mouvements de stock
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            {filteredMouvements.length} mouvements • {formatDate(new Date())}
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('table')}
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-table-line me-1"></i>Tableau
                                    </Button>
                                    <Button 
                                        variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('cards')}
                                        className="rounded-pill px-3 me-2"
                                    >
                                        <i className="ri-grid-line me-1"></i>Cartes
                                    </Button>
                                    <Button variant="outline-secondary" onClick={() => navigate('/stock/etat/instantane')}>
                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Cartes de statistiques */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '56px', 
                                                height: '56px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                boxShadow: '0 8px 16px rgba(67, 97, 238, 0.25)'
                                            }}
                                        >
                                            <i className="ri-stack-line text-white" style={{ fontSize: '28px' }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Total mouvements
                                            </span>
                                            <h3 className="stat-value text-primary mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>
                                                {filteredMouvements.length}
                                            </h3>
                                            <small className="stat-trend text-primary d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-stack-line"></i>
                                                <span className="text-truncate">tous types confondus</span>
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '56px', 
                                                height: '56px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                                boxShadow: '0 8px 16px rgba(16, 185, 129, 0.25)'
                                            }}
                                        >
                                            <i className="ri-arrow-down-line text-white" style={{ fontSize: '28px' }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Total entrées
                                            </span>
                                            <div className="d-flex align-items-baseline gap-2">
                                                <h3 className="stat-value text-success mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>
                                                    {totalEntrees}
                                                </h3>
                                                <Badge bg="success" className="px-2 py-1 rounded-pill">
                                                    +{totalEntrees}
                                                </Badge>
                                            </div>
                                            <small className="stat-trend text-success d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-arrow-down-line"></i>
                                                <span className="text-truncate">articles reçus</span>
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '56px', 
                                                height: '56px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, #dc3545, #bb2d3b)',
                                                boxShadow: '0 8px 16px rgba(220, 53, 69, 0.25)'
                                            }}
                                        >
                                            <i className="ri-arrow-up-line text-white" style={{ fontSize: '28px' }}></i>
                                        </div>
                                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                Total sorties
                                            </span>
                                            <div className="d-flex align-items-baseline gap-2">
                                                <h3 className="stat-value text-danger mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>
                                                    {totalSorties}
                                                </h3>
                                                <Badge bg="danger" className="px-2 py-1 rounded-pill">
                                                    -{totalSorties}
                                                </Badge>
                                            </div>
                                            <small className="stat-trend text-danger d-flex align-items-center gap-1 mt-1">
                                                <i className="ri-arrow-up-line"></i>
                                                <span className="text-truncate">articles sortis</span>
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filtres avancés */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-center">
                                <Col md={3}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <i className="ri-search-line text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher un produit..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="border-start-0"
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={2}>
                                    <Form.Select 
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="">📋 Tous types</option>
                                        <option value="entree">⬇️ Entrées</option>
                                        <option value="sortie">⬆️ Sorties</option>
                                        <option value="inventaire">📊 Inventaires</option>
                                        <option value="reservation">🔒 Réservations</option>
                                        <option value="annulation">❌ Annulations</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Control
                                        type="date"
                                        value={dateDebut}
                                        onChange={(e) => setDateDebut(e.target.value)}
                                        className="filter-date"
                                        placeholder="Date début"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Control
                                        type="date"
                                        value={dateFin}
                                        onChange={(e) => setDateFin(e.target.value)}
                                        className="filter-date"
                                        placeholder="Date fin"
                                    />
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex gap-2">
                                        <Form.Select 
                                            value={sortConfig.key}
                                            onChange={(e) => setSortConfig({ key: e.target.value, direction: 'desc' })}
                                            className="filter-select flex-grow-1"
                                        >
                                            <option value="date_mouvement">Trier par date</option>
                                            <option value="quantite">Trier par quantité</option>
                                        </Form.Select>
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => {
                                                setFilterText('');
                                                setFilterType('');
                                                setDateDebut('');
                                                setDateFin('');
                                            }} 
                                            className="d-flex align-items-center justify-content-center"
                                            style={{ width: '42px' }}
                                        >
                                            <i className="ri-refresh-line"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Vue Tableau avec pagination */}
                    {viewMode === 'table' && (
                        <Card className="table-card border-0">
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="mb-0 align-middle custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <SortableHeader field="date_mouvement">Date</SortableHeader>
                                                <th>Type</th>
                                                <th>Produit</th>
                                                <th>Réf.</th>
                                                <SortableHeader field="quantite" className="text-center">Quantité</SortableHeader>
                                                <th className="text-center">Stock avant</th>
                                                <th className="text-center">Stock après</th>
                                                <th>Document</th>
                                                <th>Utilisateur</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.length > 0 ? (
                                                currentItems.map((m, index) => (
                                                    <tr key={m.id_mouvement} style={{
                                                        animation: `fadeIn 0.3s ease ${index * 0.02}s both`
                                                    }}>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span className="fw-semibold">{formatDate(m.date_mouvement)}</span>
                                                                <small className="text-muted">{formatDateTime(m.date_mouvement).split(' ')[1]}</small>
                                                            </div>
                                                        </td>
                                                        <td>{getTypeBadge(m.type_mouvement)}</td>
                                                        <td>
                                                            <Link to={`/stock/produits/${m.id_produit}`} className="text-primary fw-semibold text-decoration-none">
                                                                {m.designation}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <Badge bg="light" text="dark" className="px-3 py-2 font-mono">
                                                                {m.reference}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`fw-bold fs-5 ${
                                                                m.type_mouvement === 'entree' ? 'text-success' : 
                                                                m.type_mouvement === 'sortie' ? 'text-danger' : 'text-info'
                                                            }`}>
                                                                {m.type_mouvement === 'entree' ? '+' : 
                                                                 m.type_mouvement === 'sortie' ? '-' : ''}{m.quantite}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="secondary" className="px-3 py-2">
                                                                {m.stock_avant}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="primary" className="px-3 py-2">
                                                                {m.stock_apres}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            {m.reference_document ? (
                                                                <div className="d-flex flex-column">
                                                                    <Badge bg="info" className="mb-1">
                                                                        {m.type_document}
                                                                    </Badge>
                                                                    <small className="text-muted">{m.reference_document}</small>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="ri-user-line text-muted me-2"></i>
                                                                <span>{m.prenom_utilisateur} {m.nom_utilisateur}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-5">
                                                        <div className="empty-state">
                                                            <i className="ri-history-line empty-icon text-info" style={{ fontSize: '4rem' }}></i>
                                                            <h5 className="mt-3">Aucun mouvement trouvé</h5>
                                                            <p className="text-muted mb-3">
                                                                {filterText || filterType || dateDebut || dateFin 
                                                                    ? "Aucun résultat ne correspond à vos filtres"
                                                                    : "Aucun mouvement de stock enregistré"}
                                                            </p>
                                                            {(filterText || filterType || dateDebut || dateFin) && (
                                                                <Button 
                                                                    variant="outline-primary"
                                                                    onClick={() => {
                                                                        setFilterText('');
                                                                        setFilterType('');
                                                                        setDateDebut('');
                                                                        setDateFin('');
                                                                    }}
                                                                >
                                                                    <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pagination pour la vue tableau */}
                                {totalPages > 1 && (
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                        <div className="text-muted small mb-2 mb-md-0">
                                            <i className="ri-file-list-line me-1"></i>
                                            Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredMouvements.length)} sur {filteredMouvements.length} mouvements
                                        </div>
                                        <Pagination className="mb-0 flex-wrap justify-content-center">
                                            <Pagination.First 
                                                onClick={() => handlePageChange(1)}
                                                disabled={currentPage === 1}
                                            />
                                            <Pagination.Prev 
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            />
                                            {renderPagination()}
                                            <Pagination.Next 
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            />
                                            <Pagination.Last 
                                                onClick={() => handlePageChange(totalPages)}
                                                disabled={currentPage === totalPages}
                                            />
                                        </Pagination>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue Cartes avec pagination */}
                    {viewMode === 'cards' && (
                        <>
                            <Row className="g-4">
                                {currentItems.length > 0 ? (
                                    currentItems.map((m, index) => (
                                        <Col lg={6} xl={4} key={m.id_mouvement}>
                                            <Card className="border-0 shadow-sm h-100 mouvement-card" style={{
                                                animation: `fadeIn 0.3s ease ${index * 0.05}s both`,
                                                borderRadius: '16px',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0,0,0,0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
                                            }}>
                                                <Card.Body className="p-4">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div>
                                                            <Badge 
                                                                className="mb-2 px-3 py-2"
                                                                style={{
                                                                    background: m.type_mouvement === 'entree' ? 'linear-gradient(135deg, #10b981, #059669)' :
                                                                               m.type_mouvement === 'sortie' ? 'linear-gradient(135deg, #dc3545, #bb2d3b)' :
                                                                               m.type_mouvement === 'inventaire' ? 'linear-gradient(135deg, #0dcaf0, #0b9cbe)' :
                                                                               'linear-gradient(135deg, #6c757d, #5a6268)',
                                                                    border: 'none'
                                                                }}
                                                            >
                                                                <i className={`${m.type_mouvement === 'entree' ? 'ri-arrow-down-line' :
                                                                                   m.type_mouvement === 'sortie' ? 'ri-arrow-up-line' :
                                                                                   m.type_mouvement === 'inventaire' ? 'ri-survey-line' :
                                                                                   'ri-close-line'} me-1`}></i>
                                                                {m.type_mouvement === 'entree' ? 'Entrée' :
                                                                 m.type_mouvement === 'sortie' ? 'Sortie' :
                                                                 m.type_mouvement === 'inventaire' ? 'Inventaire' :
                                                                 m.type_mouvement === 'reservation' ? 'Réservation' : 'Annulation'}
                                                            </Badge>
                                                            <h5 className="mb-1">{m.designation}</h5>
                                                            <small className="text-muted">{m.reference}</small>
                                                        </div>
                                                        <div className="text-end">
                                                            <span className={`fw-bold fs-3 ${
                                                                m.type_mouvement === 'entree' ? 'text-success' : 
                                                                m.type_mouvement === 'sortie' ? 'text-danger' : 'text-info'
                                                            }`}>
                                                                {m.type_mouvement === 'entree' ? '+' : 
                                                                 m.type_mouvement === 'sortie' ? '-' : ''}{m.quantite}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <small className="text-muted">
                                                                <i className="ri-time-line me-1"></i>
                                                                {formatDateTime(m.date_mouvement)}
                                                            </small>
                                                            <small className="text-muted">
                                                                <i className="ri-user-line me-1"></i>
                                                                {m.prenom_utilisateur} {m.nom_utilisateur}
                                                            </small>
                                                        </div>
                                                        
                                                        <div className="bg-light p-3 rounded-3">
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span className="text-muted">Stock avant</span>
                                                                <span className="fw-semibold">{m.stock_avant}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between">
                                                                <span className="text-muted">Stock après</span>
                                                                <span className="fw-semibold text-primary">{m.stock_apres}</span>
                                                            </div>
                                                        </div>

                                                        {m.reference_document && (
                                                            <div className="mt-2 small text-muted">
                                                                <i className="ri-file-text-line me-1"></i>
                                                                {m.type_document}: {m.reference_document}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <hr className="my-3" />

                                                    <div className="d-flex justify-content-end">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Voir le produit</Tooltip>}>
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                className="rounded-circle"
                                                                style={{ width: '38px', height: '38px' }}
                                                                onClick={() => navigate(`/stock/produits/${m.id_produit}`)}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))
                                ) : (
                                    <Col md={12}>
                                        <Card className="border-0 shadow-sm text-center py-5">
                                            <i className="ri-history-line fs-1 text-muted mb-3"></i>
                                            <h5>Aucun mouvement trouvé</h5>
                                        </Card>
                                    </Col>
                                )}
                            </Row>

                            {/* Pagination pour la vue cartes */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredMouvements.length)} sur {filteredMouvements.length} mouvements
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }

                    .stat-card:hover .stat-icon-wrapper {
                        transform: scale(1.1);
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .mouvement-card {
                        transition: all 0.3s ease;
                    }

                    .mouvement-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                    }

                    .sortable-header:hover {
                        color: #4361ee;
                    }

                    .search-input .form-control:focus,
                    .filter-date:focus,
                    .filter-select:focus {
                        box-shadow: none;
                        border-color: #4361ee;
                    }

                    .font-mono {
                        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
                    }

                    .stat-icon-wrapper {
                        transition: transform 0.3s ease;
                    }

                    .stat-label {
                        font-size: 0.7rem;
                        letter-spacing: 0.5px;
                    }

                    .stat-trend {
                        font-size: 0.75rem;
                    }

                    /* Styles pour la pagination */
                    .pagination {
                        gap: 5px;
                    }
                    
                    .pagination .page-item .page-link {
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                        color: #1e293b;
                        padding: 0.5rem 1rem;
                        transition: all 0.2s ease;
                        font-weight: 500;
                    }
                    
                    .pagination .page-item.active .page-link {
                        background: linear-gradient(135deg, #4361ee, #3a56d4);
                        border-color: #4361ee;
                        color: white;
                        box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                    }
                    
                    .pagination .page-item .page-link:hover {
                        background: #f1f5f9;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    }
                    
                    .pagination .page-item.disabled .page-link {
                        opacity: 0.5;
                        pointer-events: none;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}